import React, { useState, useEffect, useCallback, useRef } from 'react'

import { YStack } from 'tamagui'
import type { YStackProps } from 'tamagui'

import { Icon } from './Icon'
import { Text } from './Text'

const AUTO_COLLAPSE_MS = 10000
const ANIMATION_MS = 250

export const MobileNavDropdown = ({
  label,
  openLabel,
  icon,
  children,
  ...containerProps
}: {
  label: string
  openLabel: string
  icon: React.ReactNode
  children: React.ReactNode
} & YStackProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState(0)

  // Measure content height when open or when children change
  useEffect(() => {
    if (!isOpen || !contentRef.current) return

    const measure = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight)
      }
    }

    measure()

    // Re-measure if content changes (e.g. lazy-loaded sidebar items)
    const observer = new ResizeObserver(measure)
    observer.observe(contentRef.current)
    return () => observer.disconnect()
  }, [isOpen, children])

  // Collapse when label changes (navigating to a new page)
  useEffect(() => {
    setIsOpen(false)
  }, [label])

  // Auto-collapse after 10 seconds
  useEffect(() => {
    if (!isOpen) return

    const timeoutId = setTimeout(() => {
      setIsOpen(false)
    }, AUTO_COLLAPSE_MS)

    return () => clearTimeout(timeoutId)
  }, [isOpen])

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  return (
    <YStack
      backgroundColor='$background'
      borderRadius={18}
      // @ts-expect-error boxShadow is web-only
      boxShadow='$boxShadowLight'
      overflow='hidden'
      style={{
        willChange: 'box-shadow',
        transition: `box-shadow 375ms cubic-bezier(0,0.5,1,1), background-color ${ANIMATION_MS}ms ease`,
      }}
      hoverStyle={{
        backgroundColor: isOpen ? '$background' : '$backgroundAccent',
      }}
      {...containerProps}
    >
      <button
        type='button'
        onClick={toggle}
        aria-expanded={isOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '8px 20px',
          gap: 12,
          cursor: 'pointer',
          background: 'none',
          border: 'none',
          color: 'inherit',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {icon}

        <Text color='$color' fontWeight='$5' flex={1} textAlign='left'>
          {isOpen ? openLabel : label}
        </Text>

        <YStack
          style={{
            transition: `transform ${ANIMATION_MS}ms ease`,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <Icon name='chevron-down' color='$color' />
        </YStack>
      </button>

      <div
        ref={contentRef}
        style={{
          overflow: 'hidden',
          height: isOpen ? contentHeight : 0,
          opacity: isOpen ? 1 : 0,
          transition: `height ${ANIMATION_MS}ms ease, opacity ${ANIMATION_MS * 0.6}ms ease`,
        }}
      >
        <YStack paddingTop='$3'>{children}</YStack>
      </div>
    </YStack>
  )
}
